import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { PATHOLOGIES } from "../lib/pathologies";
import { CHAPTERS } from "../lib/chapters";
import { PATHOLOGY_TO_CHAPTERS } from "../lib/researchLinks";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const Pathologies = () => {
  const { t, lang } = useLanguage();
  const regions = Object.keys(PATHOLOGIES);
  const [activeRegion, setActiveRegion] = useState(regions[0]);

  // Resolve linked chapter for a pathology slug (first match)
  const getLinkedChapter = (slug) => {
    const ids = PATHOLOGY_TO_CHAPTERS[slug];
    if (!ids || ids.length === 0) return null;
    return CHAPTERS.find((c) => c.id === ids[0]);
  };

  return (
    <section
      id="pathologies"
      data-testid="pathologies-section"
      className="section-padding bg-white relative"
    >
      <div className="container-page">
        <div className="max-w-3xl">
          <span className="chip mb-5">
            {lang === "fr" ? "Pathologies traitées" : "Treated pathologies"}
          </span>
          <h2
            data-testid="pathologies-title"
            className="font-display font-black text-4xl sm:text-5xl text-navy leading-[1.05]"
          >
            {lang === "fr"
              ? "Un catalogue complet des pathologies du membre supérieur"
              : "A comprehensive catalog of upper-limb pathologies"}
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground/75 leading-relaxed">
            {lang === "fr"
              ? "Sélectionnez une région anatomique pour découvrir les pathologies prises en charge par le Dr Aswad, avec une description synthétique de chaque pathologie et les options thérapeutiques disponibles."
              : "Select an anatomical region to discover the pathologies managed by Dr Aswad, with a concise description of each condition and available treatment options."}
          </p>

          {/* Bridge banner — explicit cross-link to Centre bibliographique */}
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-[#FF5A1F]/30 bg-[#FFF9F4] p-4">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0A192F] text-white">
              <i className="fa-solid fa-book-open" aria-hidden="true"></i>
            </span>
            <p className="text-sm text-foreground/80 leading-relaxed flex-1">
              {lang === "fr" ? (
                <>
                  Pour chaque pathologie, deux niveaux de lecture :{" "}
                  <strong className="text-navy">la fiche patient</strong> (description simple, symptômes, traitements) et{" "}
                  <strong className="text-navy">le chapitre académique</strong> du Centre bibliographique (synthèse scientifique avec références).
                </>
              ) : (
                <>
                  For each condition, two reading levels:{" "}
                  <strong className="text-navy">the patient page</strong> (clear description, symptoms, treatments) and{" "}
                  <strong className="text-navy">the academic chapter</strong> from the Bibliography Center (scientific synthesis with references).
                </>
              )}
            </p>
            <Link
              to="/recherche"
              data-testid="pathologies-biblio-link"
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF5A1F] hover:text-navy whitespace-nowrap"
            >
              {lang === "fr" ? "Centre bibliographique" : "Bibliography Center"}
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
            </Link>
          </div>
        </div>

        <Tabs
          value={activeRegion}
          onValueChange={setActiveRegion}
          className="mt-12"
        >
          <TabsList
            data-testid="pathologies-tabs"
            className="h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0"
          >
            {regions.map((key) => {
              const region = PATHOLOGIES[key];
              const label = lang === "fr" ? region.title_fr : region.title_en;
              return (
                <TabsTrigger
                  key={key}
                  value={key}
                  data-testid={`pathology-tab-${key}`}
                  className="rounded-full border border-border bg-white px-5 py-2.5 text-sm font-display font-bold uppercase tracking-[0.12em] text-navy data-[state=active]:bg-[#0A192F] data-[state=active]:text-white data-[state=active]:border-[#0A192F] hover:border-[#FF5A1F]/40 transition-all"
                >
                  <i className={`fa-solid ${region.icon} mr-2 text-xs`} aria-hidden="true"></i>
                  {label}
                  <span className="ml-2 text-[10px] text-muted-foreground data-[state=active]:text-white/70">
                    ({region.items.length})
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {regions.map((key) => {
            const region = PATHOLOGIES[key];
            return (
              <TabsContent
                key={key}
                value={key}
                data-testid={`pathology-content-${key}`}
                className="mt-10"
              >
                <div className="grid lg:grid-cols-12 gap-8 items-start">
                  {/* Left: header card */}
                  <div className="lg:col-span-4 lg:sticky lg:top-28">
                    <div className="rounded-2xl bg-[#0A192F] p-7 text-white">
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF5A1F] text-white text-xl">
                        <i className={`fa-solid ${region.icon}`} aria-hidden="true"></i>
                      </div>
                      <h3 className="mt-5 font-display text-3xl font-bold">
                        {lang === "fr" ? region.title_fr : region.title_en}
                      </h3>
                      <p className="mt-3 text-sm text-white/70 leading-relaxed">
                        {lang === "fr"
                          ? `${region.items.length} pathologies prises en charge. Cliquez sur une pathologie pour en savoir plus.`
                          : `${region.items.length} managed conditions. Click a pathology to learn more.`}
                      </p>
                      <a
                        href="#contact"
                        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#FF5A1F] hover:text-white transition"
                      >
                        {lang === "fr" ? "Discuter de mon cas" : "Discuss my case"}
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                      </a>
                    </div>
                  </div>

                  {/* Right: accordion of pathologies */}
                  <div className="lg:col-span-8">
                    <Accordion
                      type="single"
                      collapsible
                      className="w-full rounded-2xl border border-border bg-white divide-y divide-border"
                    >
                      {region.items.map((item, i) => {
                        const chapter = getLinkedChapter(item.slug);
                        return (
                        <AccordionItem
                          key={item.slug}
                          value={item.slug}
                          data-testid={`pathology-item-${item.slug}`}
                          className="border-b-0 first:rounded-t-2xl last:rounded-b-2xl overflow-hidden"
                        >
                          <AccordionTrigger className="group px-5 py-4 hover:bg-[#F8FAFC] hover:no-underline">
                            <div className="flex items-center gap-4 text-left w-full">
                              <span className="font-display text-[11px] font-bold tracking-[0.18em] text-[#FF5A1F] w-8 shrink-0">
                                {String(i + 1).padStart(2, "0")}
                              </span>
                              <span className="font-display font-semibold text-navy text-[15px] md:text-base group-hover:text-[#FF5A1F] transition-colors flex-1">
                                {lang === "fr" ? item.name_fr : item.name_en}
                              </span>
                              {chapter && (
                                <span
                                  data-testid={`pathology-chapter-badge-${item.slug}`}
                                  title={lang === "fr" ? "Chapitre académique disponible" : "Academic chapter available"}
                                  className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded-full border"
                                  style={{ color: chapter.color, borderColor: `${chapter.color}40`, backgroundColor: `${chapter.color}10` }}
                                >
                                  <i className="fa-solid fa-book-open text-[9px]" aria-hidden="true"></i>
                                  {lang === "fr" ? `Chap. ${chapter.id}` : `Ch. ${chapter.id}`}
                                </span>
                              )}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-5 pb-5 pl-[68px] text-sm text-foreground/75 leading-relaxed">
                            <p>{lang === "fr" ? item.desc_fr : item.desc_en}</p>

                            {/* Two clear action paths — zero friction */}
                            <div className="mt-4 flex flex-wrap gap-2">
                              <Link
                                to={`/pathologie/${item.slug}`}
                                data-testid={`pathology-patient-link-${item.slug}`}
                                className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white px-4 py-2 text-xs font-semibold hover:bg-[#FF5A1F] transition-colors"
                              >
                                <i className="fa-solid fa-user" aria-hidden="true"></i>
                                {lang === "fr" ? "Fiche patient" : "Patient page"}
                                <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
                              </Link>
                              {chapter && (
                                <Link
                                  to={`/recherche/chapitre-${chapter.id}-${chapter.slug}`}
                                  data-testid={`pathology-chapter-link-${item.slug}`}
                                  className="inline-flex items-center gap-2 rounded-full border-2 px-4 py-2 text-xs font-semibold transition-colors"
                                  style={{ borderColor: chapter.color, color: chapter.color }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = chapter.color; e.currentTarget.style.color = "#fff"; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = chapter.color; }}
                                >
                                  <i className="fa-solid fa-book-open" aria-hidden="true"></i>
                                  {lang === "fr" ? `Chapitre académique — ${chapter.title}` : `Academic chapter — ${chapter.title}`}
                                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
                                </Link>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                        );
                      })}
                    </Accordion>

                    <p className="mt-5 text-xs text-muted-foreground italic">
                      {lang === "fr"
                        ? "Cette liste est indicative et non exhaustive. Pour toute question spécifique, n'hésitez pas à prendre rendez-vous."
                        : "This list is indicative and not exhaustive. For any specific question, feel free to book an appointment."}
                    </p>
                  </div>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </section>
  );
};

export default Pathologies;
